import React from 'react';

interface TimeSlotLabelProps {
  hour: number;
  minute: number;
  isHighlighted?: boolean;
  highlightedTimeSlot?: string | null;
  isOriginalDragTime?: boolean;
  originalDragTime?: string | null;
}

/**
 * Renders a time slot label with custom styling.
 * Full hour labels are displayed with full opacity, partial hours with reduced opacity.
 * Shows intermediate 5-minute intervals below each main label.
 * Highlights the label and matching intervals when isHighlighted is true.
 * Shows original drag time in red color to indicate where appointment was dragged from.
 */
const TimeSlotLabel: React.FC<TimeSlotLabelProps> = ({ 
  hour, 
  minute, 
  isHighlighted = false, 
  highlightedTimeSlot = null,
  isOriginalDragTime = false,
  originalDragTime = null
}) => {
  // Calculate the two 5-minute intervals between this label and the next
  const interval1Minute = (minute + 5) % 60;
  const interval1Hour = minute + 5 >= 60 ? hour + 1 : hour;
  
  const interval2Minute = (minute + 10) % 60;
  const interval2Hour = minute + 10 >= 60 ? hour + 1 : hour;

  // Check if each interval matches the highlighted time
  const interval1Time = `${interval1Hour.toString().padStart(2, '0')}:${interval1Minute.toString().padStart(2, '0')}:00`;
  const interval2Time = `${interval2Hour.toString().padStart(2, '0')}:${interval2Minute.toString().padStart(2, '0')}:00`;
  const isInterval1Highlighted = highlightedTimeSlot === interval1Time;
  const isInterval2Highlighted = highlightedTimeSlot === interval2Time;
  
  // Check if intervals match the original drag time
  const isInterval1OriginalDrag = originalDragTime === interval1Time;
  const isInterval2OriginalDrag = originalDragTime === interval2Time;

  // IMPORTANT: the active-state styling below NEVER changes the label's box size.
  // Previously the original-drag / highlight states added padding and grew the
  // interval font (10px -> 12px), which made the time-axis row taller. Because
  // the original-drag marker is toggled DURING a drag, that vertical growth
  // reflowed every slot below it and shifted FullCalendar's pixel->time mapping
  // out from under the cursor, making appointments drop on the wrong slot (the
  // "jump"). We now keep font-size and padding constant and convey the indicator
  // purely through colour / weight / opacity plus a box-shadow halo, which paints
  // the padded "pill" look without occupying layout space.
  return (
    <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: "2px"}}>
      <span style={{ 
        fontWeight: (isHighlighted || isOriginalDragTime) ? 'bold' : 'normal',
        fontSize: "12px",
        backgroundColor: isOriginalDragTime ? 'rgba(156, 26, 53, 0.4)' : 'transparent',
        borderRadius: '4px',
        boxShadow: isOriginalDragTime ? '0 0 0 3px rgba(156, 26, 53, 0.4)' : 'none',
        color: isOriginalDragTime ? 'white' : 'inherit',
      }}>
        {hour.toString().padStart(2, '0')}:{minute.toString().padStart(2, '0')}
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: "0px", alignItems: "center" }}>
        <span style={{ 
          fontSize: "10px", 
          opacity: (isInterval1Highlighted || isInterval1OriginalDrag) ? 1 : 0.7,
          fontWeight: (isInterval1Highlighted || isInterval1OriginalDrag) ? 'bold' : 'normal',
          backgroundColor: isInterval1OriginalDrag ? 'rgba(156, 26, 53, 0.4)' : 'transparent',
          borderRadius: '4px',
          boxShadow: isInterval1OriginalDrag ? '0 0 0 3px rgba(156, 26, 53, 0.4)' : 'none',
          color: isInterval1OriginalDrag ? 'white' : 'inherit',
        }}>
          {interval1Hour.toString().padStart(2, '0')}:{interval1Minute.toString().padStart(2, '0')}
        </span>
        <span style={{ 
          fontSize: "10px", 
          opacity: (isInterval2Highlighted || isInterval2OriginalDrag) ? 1 : 0.7,
          fontWeight: (isInterval2Highlighted || isInterval2OriginalDrag) ? 'bold' : 'normal',
          backgroundColor: isInterval2OriginalDrag ? 'rgba(156, 26, 53, 0.4)' : 'transparent',
          borderRadius: '4px',
          boxShadow: isInterval2OriginalDrag ? '0 0 0 3px rgba(156, 26, 53, 0.4)' : 'none',
          color: isInterval2OriginalDrag ? 'white' : 'inherit',
        }}>
          {interval2Hour.toString().padStart(2, '0')}:{interval2Minute.toString().padStart(2, '0')}
        </span>
      </div>
    </div>
  );
};

export default TimeSlotLabel;
